// src/components/Layout.tsx — Yak ops console chrome
import { Link, useLocation, useNavigate, Outlet } from 'react-router-dom'
import {
  Inbox,
  ListChecks,
  CheckCircle,
  Activity,
  BookOpen,
  Library,
  Briefcase,
  Settings,
  LogOut,
  ShieldCheck,
} from 'lucide-react'
import { ScriptYakLogo } from './ui/Logo'
import { Avatar } from './ui/Avatar'
import { NotificationBell } from './notifications/NotificationBell'
import { useAuthStore } from '../stores/authStore'
import { useUserClaims } from '../hooks/useUserClaims'

export function Layout() {
  const location = useLocation()
  const navigate = useNavigate()
  const { user, signOut } = useAuthStore()
  const { isSuperAdmin } = useUserClaims()

  const handleSignOut = async () => {
    try {
      await signOut()
      navigate('/login')
    } catch (error) {
      console.error('Sign out error:', error)
    }
  }

  const path = location.pathname

  // My tickets is home (`/`); a ticket detail (`/tickets/:id`) highlights it
  // too, since the home queue is where detail navigation starts. All open /
  // Closed own their own /tickets/open|closed prefixes.
  const isAllOpen = path.startsWith('/tickets/open')
  const isClosed = path.startsWith('/tickets/closed')
  const isMyTickets =
    path === '/' || (path.startsWith('/tickets/') && !isAllOpen && !isClosed)

  // Sidebar sections (UX IA order): Tickets first, then Knowledge Base, then
  // the surviving pre-pivot surfaces (Story 4.10 retired Morning Queue and
  // Escalations with the legacy stack; Customer Health and KB Updates remain —
  // see deferred-work.md), then super-admin items, then Settings.
  const navSections = [
    {
      label: 'Tickets',
      items: [
        {
          name: 'My tickets',
          href: '/',
          icon: Inbox,
          current: isMyTickets,
        },
        {
          name: 'All open',
          href: '/tickets/open',
          icon: ListChecks,
          current: isAllOpen,
        },
        {
          name: 'Closed',
          href: '/tickets/closed',
          icon: CheckCircle,
          current: isClosed,
        },
      ],
    },
    {
      label: null,
      items: [
        {
          name: 'Knowledge Base',
          href: '/kb',
          icon: Library,
          current: path === '/kb' || path.startsWith('/kb/'),
        },
      ],
    },
    {
      label: null,
      items: [
        {
          name: 'Customer Health',
          href: '/health',
          icon: Activity,
          current: path === '/health',
        },
        {
          name: 'KB Updates',
          href: '/kb-updates',
          icon: BookOpen,
          current: path === '/kb-updates',
        },
        ...(isSuperAdmin
          ? [
              {
                name: 'Prospects',
                href: '/prospects',
                icon: Briefcase,
                current: path === '/prospects',
              },
              {
                name: 'Super Admin',
                href: '/super-admin',
                icon: ShieldCheck,
                current: path.startsWith('/super-admin'),
              },
            ]
          : []),
        {
          name: 'Settings',
          href: '/settings',
          icon: Settings,
          current: path.startsWith('/settings'),
        },
      ],
    },
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="dashboard-header">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <ScriptYakLogo className="h-4" />
          </div>
          <div className="flex items-center space-x-4">
            <NotificationBell />
            {user?.organization?.name && (
              <div className="flex items-center space-x-2 text-sm text-gray-600">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                <span>{user.organization.name}</span>
              </div>
            )}
            <div className="flex items-center space-x-2">
              <Avatar user={user || undefined} size="sm" />
              <div className="text-sm">
                <div className="font-medium text-gray-900">
                  {user?.first_name && user?.last_name
                    ? `${user.first_name} ${user.last_name}`
                    : user?.email?.split('@')[0] || 'User'}
                </div>
                <div className="text-gray-500">{user?.email}</div>
              </div>
              <button
                onClick={handleSignOut}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                title="Sign out"
              >
                <LogOut className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex">
        {/* Sidebar */}
        <aside className="dashboard-sidebar">
          <nav className="p-4 space-y-4">
            {navSections.map((section, sectionIndex) => (
              <div key={section.label ?? `section-${sectionIndex}`}>
                {section.label && (
                  <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {section.label}
                  </div>
                )}
                <div className="space-y-1">
                  {section.items.map(item => {
                    const Icon = item.icon
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        aria-current={item.current ? 'page' : undefined}
                        className={
                          item.current
                            ? 'sidebar-nav-link-active'
                            : 'sidebar-nav-link'
                        }
                      >
                        <Icon className="w-5 h-5" />
                        <span>{item.name}</span>
                      </Link>
                    )
                  })}
                </div>
              </div>
            ))}
          </nav>
        </aside>

        {/* Main Content */}
        <main className="dashboard-main">
          <Outlet /> {/* This renders the child routes */}
        </main>
      </div>
    </div>
  )
}
